import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { Link } from 'react-router-dom';

function EditProfileForm() {
  const { token } = useAuth();
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      const API_URL = 'https://members.abssvss.com/members_api/get_user_details.php';
      try {
        const response = await fetch(API_URL, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Could not fetch user details.');
        const data = await response.json();
        setFormData(data);
      } catch (error) {
        setMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserDetails();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    const API_URL = 'https://members.abssvss.com/members_api/submit_profile_update.php';
    try {
        const response = await fetch(API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(formData)
        });
        const result = await response.json();
        if(!response.ok) throw new Error(result.message);

        setMessage(result.message);
        setIsSuccess(true);
    } catch (error) {
        setMessage(error.message);
        setIsSuccess(false);
    } finally {
        setIsSubmitting(false);
    }
  };

  if (isLoading) return <p className="text-center">Loading your profile...</p>;
  if (!formData) return <p className="text-center text-red-500">{message || 'Could not load profile data.'}</p>;

  return (
    <div className="bg-white p-8 rounded-lg shadow-lg w-full max-w-2xl border-t-4 border-brand-primary">
      <h2 className="text-3xl font-bold text-brand-primary text-center mb-6">Edit Your Profile</h2>
      
      {isSuccess ? (
        <div className="text-center">
            <p className="text-lg text-green-700">{message}</p>
            <Link to="/my-dashboard" className="block text-center text-white bg-brand-primary hover:bg-blue-900 mt-4 py-2 px-4 rounded-md">
                Back to Dashboard
            </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" required className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" required className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
          </div>
          <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" required readOnly className="w-full p-3 bg-gray-200 text-gray-500 rounded-md border border-gray-300 cursor-not-allowed" title="Email cannot be changed." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="tel" name="phoneNumber" value={formData.phoneNumber || ''} onChange={handleChange} placeholder="Phone Number" className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            <input type="text" name="state" value={formData.state} onChange={handleChange} placeholder="State" required className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
          </div>
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" name="city" value={formData.city || ''} onChange={handleChange} placeholder="City" className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
            <input type="text" name="position" value={formData.position || ''} onChange={handleChange} placeholder="Position" className="p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary" />
          </div>
          <textarea name="bio" value={formData.bio || ''} onChange={handleChange} placeholder="A short bio about yourself..." rows="4" className="w-full p-3 bg-gray-50 text-gray-800 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-primary"></textarea>
          
          <button type="submit" disabled={isSubmitting} className="w-full p-3 bg-brand-primary hover:bg-blue-900 rounded-md font-bold text-white transition-colors disabled:bg-gray-400">
            {isSubmitting ? 'Submitting...' : 'Submit Changes for Approval'}
          </button>
          <Link to="/my-dashboard" className="block text-center text-gray-600 hover:text-brand-primary mt-2">
              Cancel
          </Link>
          {message && !isSuccess && <p className="mt-4 text-center text-red-600">{message}</p>}
        </form>
      )}
    </div>
  );
}

export default EditProfileForm;

